import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { PageHeader } from "../components/TaskTable";
import { useData } from "../lib/DataContext";
import { useAuth } from "../lib/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "../components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Progress } from "../components/ui/progress";
import { toast } from "sonner";
import { Plus, FolderKanban, Pencil, Trash2, Star, Eye, EyeOff, ChevronUp, ChevronDown, Share2, X, Settings } from "lucide-react";

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(null);
  const [sharing, setSharing] = useState(null);
  const { users, favourites, toggleFavourite } = useData();
  const { user } = useAuth();
  const nav = useNavigate();
  const [showHidden, setShowHidden] = useState(false);
  const [showFav, setShowFav] = useState(false);
  const [wsMap, setWsMap] = useState({});
  const canCreate = ["master_admin", "admin", "manager"].includes(user?.role);
  const canDelete = ["master_admin", "admin"].includes(user?.role);
  const canManage = ["master_admin", "admin", "manager"].includes(user?.role);
  const load = () => api.get("/projects", { params: { show_hidden: showHidden, favourite: showFav } }).then((r) => setProjects(r.data));
  useEffect(() => { load(); }, [showHidden, showFav]); // eslint-disable-line
  useEffect(() => {
    api.get("/workspaces").then((r) => {
      const m = {};
      r.data.forEach((w) => { if (w.project_id) m[w.project_id] = w.id; });
      setWsMap(m);
    }).catch(() => {});
  }, []);

  const hideToggle = async (p) => {
    try {
      await api.post(`/projects/${p.id}/${p.is_hidden ? "unhide" : "hide"}`);
      toast.success(p.is_hidden ? "Project unhidden" : "Project hidden"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const move = async (p, direction) => {
    try { await api.post(`/projects/${p.id}/reorder`, { direction }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Cannot move"); }
  };

  const del = async (p) => {
    if (!window.confirm(`Delete project "${p.name}" (${p.human_id})?`)) return;
    try {
      await api.delete(`/projects/${p.id}`);
      toast.success("Project deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete project");
    }
  };

  return (
    <div className="p-4 sm:p-6">
      <PageHeader title="Projects" subtitle={`${projects.length} projects`}>
        <button data-testid="projects-filter-favourites" onClick={() => setShowFav((s) => !s)}
          className={`flex items-center gap-1 h-9 px-2.5 rounded-md border text-sm ${showFav ? "bg-amber-50 border-amber-300 text-amber-700" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
          <Star size={14} className={showFav ? "fill-amber-500 text-amber-500" : ""} /> Favourites
        </button>
        <button data-testid="projects-show-hidden" onClick={() => setShowHidden((s) => !s)}
          className={`flex items-center gap-1 h-9 px-2.5 rounded-md border text-sm ${showHidden ? "bg-slate-800 border-slate-800 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
          <Eye size={14} /> {showHidden ? "Showing Hidden" : "Show Hidden"}
        </button>
        {canCreate && <Button onClick={() => { setEditing(null); setShow(true); }} data-testid="new-project-button"><Plus size={16} className="mr-1" /> New Project</Button>}
      </PageHeader>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
        {projects.map((p, idx) => (
          <div key={p.id} data-testid={`project-card-${p.human_id}`} className="bg-white border border-slate-200 rounded-xl px-4 py-4 shadow-sm hover:shadow-md hover:border-slate-300 transition-all">
            <div className="flex items-start gap-2.5">
              <button data-testid={`project-fav-${p.human_id}`} onClick={() => toggleFavourite("project", p.id)} title="Favourite"
                className={`mt-0.5 h-7 w-7 inline-flex items-center justify-center rounded-md shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${favourites.project.has(p.id) ? "text-amber-500" : "text-slate-300 hover:text-amber-400 hover:bg-amber-50"}`}>
                <Star size={15} className={favourites.project.has(p.id) ? "fill-amber-500" : ""} />
              </button>
              <div className="h-9 w-9 rounded-lg bg-violet-100 flex items-center justify-center shrink-0"><FolderKanban size={18} className="text-violet-600" /></div>
              <div className="min-w-0 flex-1">
                <div className="tm-heading text-[15px] font-semibold text-slate-900 truncate flex items-center gap-1.5" title={p.name}>
                  {p.is_hidden && <span data-testid={`project-hidden-badge-${p.human_id}`} className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-slate-500 bg-slate-100 rounded px-1 py-0.5"><EyeOff size={10} /> Hidden</span>}
                  {p.name}
                </div>
                <div className="tm-body text-xs text-slate-400 font-mono mt-0.5 truncate">{p.code || ""}</div>
              </div>
              <div className="flex items-center gap-2 shrink-0 mt-1">
                <span className={`tm-badge inline-block text-[10px] font-semibold rounded-full px-2 py-0.5 ${(p.status || "Active") === "Active" ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-500"}`}>{p.status || "Active"}</span>
                {canManage && <span className="text-slate-300 select-none" aria-hidden="true">|</span>}
                {canManage && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button data-testid={`project-settings-${p.human_id}`} title="Project settings" className="h-7 w-7 inline-flex items-center justify-center rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"><Settings size={15} /></button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                      <DropdownMenuItem data-testid={`project-move-up-${p.human_id}`} disabled={idx === 0} onClick={() => move(p, "up")}><ChevronUp size={14} className="mr-2" /> Move up</DropdownMenuItem>
                      <DropdownMenuItem data-testid={`project-move-down-${p.human_id}`} disabled={idx === projects.length - 1} onClick={() => move(p, "down")}><ChevronDown size={14} className="mr-2" /> Move down</DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem data-testid={`project-share-${p.human_id}`} onClick={() => setSharing(p)}><Share2 size={14} className="mr-2" /> Share</DropdownMenuItem>
                      <DropdownMenuItem data-testid={`project-edit-${p.human_id}`} onClick={() => { setEditing(p); setShow(true); }}><Pencil size={14} className="mr-2" /> Edit</DropdownMenuItem>
                      <DropdownMenuItem data-testid={`project-hide-${p.human_id}`} onClick={() => hideToggle(p)}>{p.is_hidden ? <Eye size={14} className="mr-2" /> : <EyeOff size={14} className="mr-2" />} {p.is_hidden ? "Unhide" : "Hide"}</DropdownMenuItem>
                      {canDelete && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem data-testid={`project-delete-${p.human_id}`} className="text-red-600 focus:text-red-600" onClick={() => del(p)}><Trash2 size={14} className="mr-2" /> Delete</DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            </div>
            <div className="mt-4">
              <div className="flex justify-between text-xs text-slate-500 mb-1"><span className="tm-body">Progress</span><span className="font-medium text-slate-700">{p.stats.progress}%</span></div>
              <Progress value={p.stats.progress} className="h-2" />
            </div>
            <div className="grid grid-cols-4 gap-1 mt-4 text-center">
              {[["Total", p.stats.total_tasks], ["Open", p.stats.open], ["Overdue", p.stats.overdue], ["Blocked", p.stats.blocked]].map(([l, v]) => (
                <div key={l}><div className="text-base font-semibold text-slate-900 leading-5">{v}</div><div className="tm-body text-[10px] text-slate-500 mt-0.5">{l}</div></div>
              ))}
            </div>
            <div className="flex gap-2 mt-4 border-t border-slate-100 pt-3">
              <Button variant="outline" size="sm" className="flex-1 h-8 rounded-lg text-sm font-medium" data-testid={`project-view-tasks-${p.human_id}`} onClick={() => nav(`/tasks?project_id=${p.id}`)}>View Tasks</Button>
              {wsMap[p.id] && (
                <Button variant="outline" size="sm" className="flex-1 h-8 rounded-lg text-sm font-medium" data-testid={`project-open-ws-${p.human_id}`} onClick={() => nav(`/workspaces/${wsMap[p.id]}`)}>Open Workspace</Button>
              )}
            </div>
          </div>
        ))}
      </div>
      {show && <ProjectModal users={users} project={editing} onClose={() => { setShow(false); setEditing(null); }} onDone={() => { setShow(false); setEditing(null); load(); }} />}
      {sharing && <ShareProjectModal users={users} project={sharing} onClose={() => setSharing(null)} />}
    </div>
  );
}

function ShareProjectModal({ users, project, onClose }) {
  const [shares, setShares] = React.useState([]);
  const [form, setForm] = React.useState({ shared_with_user_id: "", permission: "view", expires_at: "" });
  const load = React.useCallback(() => api.get(`/shares?entity_type=project&entity_id=${project.id}`).then((r) => setShares(r.data)).catch(() => {}), [project.id]);
  React.useEffect(() => { load(); }, [load]);
  const submit = async () => {
    if (!form.shared_with_user_id) return toast.error("Select a user");
    try { await api.post("/shares", { entity_type: "project", entity_id: project.id, ...form, expires_at: form.expires_at || null }); toast.success("Shared"); setForm({ shared_with_user_id: "", permission: "view", expires_at: "" }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Share failed"); }
  };
  const revoke = async (id) => { await api.post(`/shares/${id}/revoke`); toast.success("Revoked"); load(); };
  const changePerm = async (s, permission) => { await api.put(`/shares/${s.id}`, { permission, expires_at: s.expires_at || null }); load(); };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="project-share-modal">
        <DialogHeader><DialogTitle>Share: {project.name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-slate-400">Sharing does not change ownership. Access is enforced server-side.</p>
          <Select value={form.shared_with_user_id} onValueChange={(v) => setForm({ ...form, shared_with_user_id: v })}>
            <SelectTrigger data-testid="project-share-user"><SelectValue placeholder="Select user…" /></SelectTrigger>
            <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.full_name} ({u.email})</SelectItem>)}</SelectContent>
          </Select>
          <div className="flex gap-2">
            <Select value={form.permission} onValueChange={(v) => setForm({ ...form, permission: v })}>
              <SelectTrigger className="flex-1" data-testid="project-share-perm"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="view">View</SelectItem><SelectItem value="edit">Edit</SelectItem><SelectItem value="manage">Manage</SelectItem></SelectContent>
            </Select>
            <Input type="datetime-local" className="flex-1" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} data-testid="project-share-expiry" />
          </div>
          <Button className="w-full" onClick={submit} data-testid="project-share-submit"><Share2 size={14} className="mr-1.5" />Share</Button>
          <div className="border-t pt-3 space-y-2 max-h-56 overflow-auto">
            <div className="text-xs font-semibold text-slate-500">Current Shares ({shares.length})</div>
            {shares.length === 0 && <div className="text-sm text-slate-400">Not shared yet.</div>}
            {shares.map((s) => (
              <div key={s.id} data-testid={`project-share-row-${s.id}`} className="flex items-center gap-2 text-sm border rounded-lg p-2">
                <div className="min-w-0 flex-1"><div className="truncate text-slate-700">{s.shared_with_name}</div><div className="text-[11px] text-slate-400">{s.shared_with_email} · <span className={s.status === "active" ? "text-emerald-600" : "text-red-500"}>{s.status}</span></div></div>
                {s.status === "active" && <>
                  <Select value={s.permission} onValueChange={(v) => changePerm(s, v)}><SelectTrigger className="h-8 w-24 text-xs"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="view">View</SelectItem><SelectItem value="edit">Edit</SelectItem><SelectItem value="manage">Manage</SelectItem></SelectContent></Select>
                  <button onClick={() => revoke(s.id)} data-testid={`project-share-revoke-${s.id}`} className="p-1.5 rounded text-slate-400 hover:text-red-600 hover:bg-red-50"><X size={14} /></button>
                </>}
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ProjectModal({ users, project, onClose, onDone }) {
  const [f, setF] = useState({
    name: project?.name || "", code: project?.code || "", description: project?.description || "",
    head_id: project?.head_id || "", start_date: project?.start_date || "",
    target_completion_date: project?.target_completion_date || "", location: project?.location || "",
  });
  const submit = async () => {
    if (!f.name) return toast.error("Name required");
    const payload = { ...f }; Object.keys(payload).forEach((k) => payload[k] === "" && (payload[k] = null)); payload.name = f.name;
    if (project) {
      await api.put(`/projects/${project.id}`, payload);
      toast.success("Project updated");
    } else {
      const { data } = await api.post("/projects", payload);
      toast.success("Project created");
      if (window.confirm(`Create a matching workspace for "${data.name}"?`)) {
        try {
          await api.post("/workspaces", { name: data.name, description: data.description || "", project_id: data.id, department_id: data.department_id || null });
          toast.success("Workspace created and linked to this project");
        } catch { toast.error("Project created, but the workspace could not be created"); }
      }
    }
    onDone();
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="project-modal">
        <DialogHeader><DialogTitle>{project ? "Edit Project" : "New Project"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Name *</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Code</Label><Input value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} /></div>
            <div><Label>Location</Label><Input value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} /></div>
          </div>
          <div><Label>Description</Label><Textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></div>
          <div><Label>Project Head</Label>
            <Select value={f.head_id} onValueChange={(v) => setF({ ...f, head_id: v })}>
              <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Start Date</Label><Input type="date" value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value })} /></div>
            <div><Label>Target Date</Label><Input type="date" value={f.target_completion_date} onChange={(e) => setF({ ...f, target_completion_date: e.target.value })} /></div>
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={submit} data-testid="project-submit">{project ? "Save" : "Create"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
